import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Alert,
  AlertDescription,
} from '../../components/ui/alert';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import {
  BadgeCheck,
  Shield,
  Store,
  Award,
  FileText,
  Upload,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Info,
  Building,
  Calendar,
  MapPin,
  Sparkles
} from 'lucide-react';

const VERIFICATION_STATUS_CONFIG = {
  pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Pending Review' },
  under_review: { color: 'bg-blue-100 text-blue-700', icon: AlertCircle, label: 'Under Review' },
  approved: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Approved' },
  rejected: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Rejected' }
};

const BrandRegistry = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [loading, setLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('status');
  
  // Blue Tick form
  const [bluTickForm, setBluTickForm] = useState({
    brand_name: '',
    tm_status: '',
    tm_number: '',
    tm_certificate_url: '',
    additional_notes: ''
  });
  const [bluTickSubmitting, setBluTickSubmitting] = useState(false);
  
  // Green Tick form
  const [greenTickForm, setGreenTickForm] = useState({
    shop_license_url: '',
    electricity_bill_url: '',
    signage_photo_url: '',
    store_address: '',
    store_established_year: '',
    additional_notes: ''
  });
  const [greenTickSubmitting, setGreenTickSubmitting] = useState(false);
  
  // Fetch verification status
  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/brand-registry/verification/status');
      setVerificationStatus(data);
    } catch (error) {
      console.error('Error fetching verification status:', error);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Submit Blue Tick verification
  const handleBluTickSubmit = async () => {
    if (!bluTickForm.brand_name || !bluTickForm.tm_status || !bluTickForm.tm_number || !bluTickForm.tm_certificate_url) {
      toast({
        title: 'Missing Information',
        description: 'Please fill all required fields',
        variant: 'destructive'
      });
      return;
    }

    try {
      setBluTickSubmitting(true);
      await apiCall('/api/brand-registry/verification/brand-owner', {
        method: 'POST',
        body: JSON.stringify(bluTickForm)
      });
      
      toast({
        title: '✅ Request Submitted',
        description: 'Your brand owner verification request has been submitted for review.'
      });
      
      setBluTickForm({
        brand_name: '',
        tm_status: '',
        tm_number: '',
        tm_certificate_url: '',
        additional_notes: ''
      });
      
      fetchStatus();
      setActiveTab('status');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit verification request',
        variant: 'destructive'
      });
    } finally {
      setBluTickSubmitting(false);
    }
  };

  // Submit Green Tick verification
  const handleGreenTickSubmit = async () => {
    if (!greenTickForm.shop_license_url || !greenTickForm.store_address || !greenTickForm.store_established_year) {
      toast({
        title: 'Missing Information',
        description: 'Please fill all required fields',
        variant: 'destructive'
      });
      return;
    }

    const currentYear = new Date().getFullYear();
    if (currentYear - parseInt(greenTickForm.store_established_year) < 3) {
      toast({
        title: 'Eligibility Issue',
        description: 'Store must be at least 3 years old to qualify for Legacy Store verification',
        variant: 'destructive'
      });
      return;
    }

    try {
      setGreenTickSubmitting(true);
      await apiCall('/api/brand-registry/verification/legacy-store', {
        method: 'POST',
        body: JSON.stringify({
          ...greenTickForm,
          store_established_year: parseInt(greenTickForm.store_established_year)
        })
      });
      
      toast({
        title: '✅ Request Submitted',
        description: 'Your legacy store verification request has been submitted for review.'
      });
      
      setGreenTickForm({
        shop_license_url: '',
        electricity_bill_url: '',
        signage_photo_url: '',
        store_address: '',
        store_established_year: '',
        additional_notes: ''
      });
      
      fetchStatus();
      setActiveTab('status');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit verification request',
        variant: 'destructive'
      });
    } finally {
      setGreenTickSubmitting(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const config = VERIFICATION_STATUS_CONFIG[status] || VERIFICATION_STATUS_CONFIG.pending;
    const Icon = config.icon;
    return (
      <Badge className={config.color}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  const hasBlueTick = verificationStatus?.badges?.is_brand_owner;
  const hasGreenTick = verificationStatus?.badges?.is_legacy_store;
  const hasPendingBlue = verificationStatus?.pending_requests?.some(r => r.verification_type === 'brand_owner');
  const hasPendingGreen = verificationStatus?.pending_requests?.some(r => r.verification_type === 'legacy_store');

  return (
    <DashboardLayout>
      <div className="p-3 md:p-6 max-w-4xl mx-auto space-y-4 md:space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-600" />
            Brand Registry & Verification
          </h1>
          <p className="text-muted-foreground mt-1">
            Protect your brand identity and build customer trust
          </p>
        </div>

        {/* Current Badges */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Your Verification Status</CardTitle>
            <CardDescription>
              Badges appear on your store and product pages
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 md:gap-6">
              {/* Blue Tick */}
              <div className={`flex-1 p-4 rounded-lg border-2 ${hasBlueTick ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-full ${hasBlueTick ? 'bg-blue-500' : 'bg-gray-200'}`}>
                    <BadgeCheck className={`h-6 w-6 ${hasBlueTick ? 'text-white' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <div className="font-semibold flex items-center gap-2">
                      Verified Brand Owner
                      {hasBlueTick && <span className="text-blue-500">✓</span>}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {hasBlueTick ? 'Trademark verified' : 'For trademark holders'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Green Tick */}
              <div className={`flex-1 p-4 rounded-lg border-2 ${hasGreenTick ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-full ${hasGreenTick ? 'bg-green-500' : 'bg-gray-200'}`}>
                    <Store className={`h-6 w-6 ${hasGreenTick ? 'text-white' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <div className="font-semibold flex items-center gap-2">
                      Verified Legacy Store
                      {hasGreenTick && <span className="text-green-500">✓</span>}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {hasGreenTick ? 'Physical store verified' : 'For established physical stores'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 text-xs sm:text-sm">
            <TabsTrigger value="status">
              <Clock className="h-4 w-4 mr-2" />
              Status
            </TabsTrigger>
            <TabsTrigger value="blue-tick" disabled={hasBlueTick || hasPendingBlue}>
              <BadgeCheck className="h-4 w-4 mr-2" />
              Blue Tick
            </TabsTrigger>
            <TabsTrigger value="green-tick" disabled={hasGreenTick || hasPendingGreen}>
              <Store className="h-4 w-4 mr-2" />
              Green Tick
            </TabsTrigger>
          </TabsList>

          {/* Status Tab */}
          <TabsContent value="status" className="space-y-4">
            {/* Pending Requests */}
            {verificationStatus?.pending_requests?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Pending Requests</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {verificationStatus.pending_requests.map((req) => (
                    <div key={req.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {req.verification_type === 'brand_owner' ? (
                          <BadgeCheck className="h-5 w-5 text-blue-500" />
                        ) : (
                          <Store className="h-5 w-5 text-green-500" />
                        )}
                        <div>
                          <div className="font-medium">
                            {req.verification_type === 'brand_owner' ? 'Brand Owner Verification' : 'Legacy Store Verification'}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Submitted on {new Date(req.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      {getStatusBadge(req.status)}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Approved Verifications */}
            {verificationStatus?.approved_verifications?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Approved Verifications</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {verificationStatus.approved_verifications.map((req) => (
                    <div key={req.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <div>
                          <div className="font-medium">
                            {req.verification_type === 'brand_owner' ? (
                              <>Brand: {req.brand_name}</>
                            ) : (
                              'Legacy Store'
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Approved on {new Date(req.reviewed_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      {getStatusBadge('approved')}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* No verifications */}
            {!verificationStatus?.pending_requests?.length && !verificationStatus?.approved_verifications?.length && (
              <Card className="p-4 md:p-8 text-center">
                <Sparkles className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-700">No Verifications Yet</h3>
                <p className="text-muted-foreground mt-2">
                  Apply for Blue Tick or Green Tick to build customer trust
                </p>
              </Card>
            )}
          </TabsContent>

          {/* Blue Tick Tab */}
          <TabsContent value="blue-tick">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BadgeCheck className="h-5 w-5 text-blue-500" />
                  Brand Owner Verification (Blue Tick)
                </CardTitle>
                <CardDescription>
                  For D2C brands and trademark holders. Proves you own the brand's intellectual property.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    You need a registered trademark (®) or pending trademark application (™) to apply.
                  </AlertDescription>
                </Alert>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Brand Name *</Label>
                    <Input
                      placeholder="e.g., Nike, Noise, boAt"
                      value={bluTickForm.brand_name}
                      onChange={(e) => setBluTickForm({ ...bluTickForm, brand_name: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Trademark Status *</Label>
                    <Select
                      value={bluTickForm.tm_status}
                      onValueChange={(v) => setBluTickForm({ ...bluTickForm, tm_status: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="REGISTERED">Registered (®) - Certificate issued</SelectItem>
                        <SelectItem value="APPLIED">Applied (™) - Application pending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Trademark Number *</Label>
                    <Input
                      placeholder="e.g., 4523678"
                      value={bluTickForm.tm_number}
                      onChange={(e) => setBluTickForm({ ...bluTickForm, tm_number: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Registration number (if ®) or Application number (if ™)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Certificate URL *</Label>
                    <Input
                      placeholder="https://..."
                      value={bluTickForm.tm_certificate_url}
                      onChange={(e) => setBluTickForm({ ...bluTickForm, tm_certificate_url: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Upload your trademark certificate to cloud storage and paste the URL
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Additional Notes</Label>
                    <Textarea
                      placeholder="Any additional information..."
                      value={bluTickForm.additional_notes}
                      onChange={(e) => setBluTickForm({ ...bluTickForm, additional_notes: e.target.value })}
                    />
                  </div>
                </div>

                <Button 
                  onClick={handleBluTickSubmit} 
                  disabled={bluTickSubmitting}
                  className="w-full"
                >
                  {bluTickSubmitting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <BadgeCheck className="h-4 w-4 mr-2" />
                  )}
                  Submit for Verification
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Green Tick Tab */}
          <TabsContent value="green-tick">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Store className="h-5 w-5 text-green-500" />
                  Legacy Store Verification (Green Tick)
                </CardTitle>
                <CardDescription>
                  For established physical stores. Proves you are a legitimate, long-standing business.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Your store must be at least 3 years old and have a valid Shop & Establishment License.
                  </AlertDescription>
                </Alert>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Shop & Establishment License URL *</Label>
                    <Input
                      placeholder="https://..."
                      value={greenTickForm.shop_license_url}
                      onChange={(e) => setGreenTickForm({ ...greenTickForm, shop_license_url: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Upload your Gumastha/Shop License (must be 3+ years old)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Store Address *</Label>
                    <Textarea
                      placeholder="Full address including city and pincode"
                      value={greenTickForm.store_address}
                      onChange={(e) => setGreenTickForm({ ...greenTickForm, store_address: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Year Established *</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 2010"
                      min="1900"
                      max={new Date().getFullYear() - 3}
                      value={greenTickForm.store_established_year}
                      onChange={(e) => setGreenTickForm({ ...greenTickForm, store_established_year: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Electricity Bill URL (Optional)</Label>
                    <Input
                      placeholder="https://..."
                      value={greenTickForm.electricity_bill_url}
                      onChange={(e) => setGreenTickForm({ ...greenTickForm, electricity_bill_url: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Recent electricity bill showing the store address
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Store Signage Photo URL (Optional)</Label>
                    <Input
                      placeholder="https://..."
                      value={greenTickForm.signage_photo_url}
                      onChange={(e) => setGreenTickForm({ ...greenTickForm, signage_photo_url: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Geotagged photo of your store's signboard
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Additional Notes</Label>
                    <Textarea
                      placeholder="Any additional information about your store's history..."
                      value={greenTickForm.additional_notes}
                      onChange={(e) => setGreenTickForm({ ...greenTickForm, additional_notes: e.target.value })}
                    />
                  </div>
                </div>

                <Button 
                  onClick={handleGreenTickSubmit} 
                  disabled={greenTickSubmitting}
                  className="w-full"
                >
                  {greenTickSubmitting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Store className="h-4 w-4 mr-2" />
                  )}
                  Submit for Verification
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default BrandRegistry;
